import every from 'lodash.every';
import get from 'lodash.get';
import VueResource from 'vue-resource';
import VeeValidate from 'vee-validate';

import Banners from './mixins/banners';
import Error from './mixins/error';
import SSO from './mixins/sso';
import V1 from './mixins/v1';
import V2 from './mixins/v2';

import getQueryParams from './support/get_query_params';
import Logger from './support/logger';

const { Vue, cookieDomain, setcookie } = window;
const log = new Logger();

function initLogin() {
  Vue.use(VueResource);
  Vue.use(VeeValidate);
  Vue.config.productionTip = false;

  window.volusionLogin = new Vue({
    el: '#main',
    http: {
      headers: {
        'Content-Type': 'application/json'
      }
    },
    mixins: [
      Error, V1, V2, SSO, Banners
    ],
    data: {
      choices: {},
      loginData: {
        username: '',
        password: ''
      },
      processing: false,
      step: 'login',
      submitBtnTxt: 'Submit',
      urlParams: getQueryParams()
    },
    methods: {
      checkForLoginRedirect(name) {
        const cookieName = name || 'loginredirect';

        if (!this.urlParams[cookieName]) {
          return;
        }

        log.debug({
          category: 'support',
          message: 'checkForLoginRedirect',
          data: {
            cookieName,
            cookieValue: this.urlParams[cookieName]
          }
        });
        setcookie(cookieName, this.urlParams[cookieName], 1, cookieDomain, false);
      },
      forgotPassword(version) {
        log.debug({
          category: 'app',
          message: 'forgotPassword',
          data: { version }
        });
        this[version](this).forgotPassword();
      },
      hasMultipleChoices() {
        const hasChoices = every(this.choices, choice => choice !== false);
        log.debug({
          category: 'app',
          message: 'hasMultipleChoices',
          data: {
            hasChoices,
            choices: this.choices
          }
        });
        return hasChoices;
      },
      initForm() {
        log.debug({
          category: 'app',
          message: 'initForm'
        });

        if (this.urlParams.step) {
          this.step = this.urlParams.step;
        }

        if (this.urlParams.email) {
          this.loginData.username = decodeURIComponent(this.urlParams.email);
        }
      },
      processLoginResults([v1, v2]) {
        log.debug({
          category: 'app',
          message: 'processLoginResults',
          data: { v1, v2 }
        });
        this.choices = { v1, v2 };
        this.processing = false;

        if (this.hasMultipleChoices()) {
          this.step = 'select';
          return;
        }

        if (this.choices.v1) {
          this.v1(this).redirect(this.choices.v1);
          return;
        }

        if (this.choices.v2) {
          this.v2(this).recordActivity(this.choices.v2);
          return;
        }

        // If we've made it this far they have no available options, so we need to show an error message
        this.errorObj = {
          status: 401,
          message: 'Not found'
        };
      },
      processValidateResult(result) {
        log.debug({
          category: 'app',
          message: 'processValidateResult',
          data: { result }
        });
        if (!result) {
          this.processing = false;
          return;
        }

        Promise.all([
          this.v1(this).login(),
          this.v2(this).login()
        ])
          .then(this.processLoginResults)
          .catch((error) => {
            log.exception(error);
          });
      },
      reset() { // This will only be called by V2 at the moment
        log.debug({
          category: 'app',
          message: 'reset'
        });
        this.v2(this).resetPassword();
      },
      select(version) {
        if (version === 'v1') {
          this.v1(this).redirect(this.choices[version]);
          return;
        }

        this.v2(this).recordActivity(this.choices.v2);
      },
      showIntercom() {
        const Intercom = get(window, 'Intercom', false);
        log.debug({
          category: 'app',
          message: 'showIntercom',
          data: {
            intercomExists: Intercom
          }
        });
        if (!Intercom) return;
        Intercom('showNewMessage');
      },
      submit() {
        log.debug({
          category: 'app',
          message: 'submit'
        });
        this.processing = true;
        this.errorMsg = '';
        this.$validator.validateAll()
          .then(this.processValidateResult);
      }
    },
    beforeMount() {
      this.initForm();
      this.checkForLoginRedirect();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLogin);
} else {
  initLogin();
}
